<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { getProductionOrder, getProductionOrderStages, listProductionLots } from '../productService'
import type { ProductionLot, ProductionOrder, ProductionOrderStage } from '../types'
import { buildHandoverText, buildProductionOrderCsv, completionPercent as calculateCompletionPercent, evaluateProductionScheduleRisk, findBottleneckStage, stageCompletionPercent, summarizeStageExceptions } from '../productionReporting'

const route = useRoute(), client = useApiClient(), id = computed(() => Number(route.params.id))
const order = ref<ProductionOrder | null>(null), stages = ref<ProductionOrderStage[]>([]), lots = ref<ProductionLot[]>([])
const loading = ref(false), error = ref(''), handoverAt = ref(''), exportMessage = ref('')
const produced = computed(() => Number(order.value?.produced_quantity || 0))
const planned = computed(() => Number(order.value?.planned_quantity || 0))
const remaining = computed(() => Math.max(0, planned.value - produced.value))
const completionPercent = computed(() => calculateCompletionPercent(produced.value, planned.value))
const exceptionSummary = computed(() => summarizeStageExceptions(stages.value))
const rejectedTotal = computed(() => exceptionSummary.value.rejected)
const reworkTotal = computed(() => exceptionSummary.value.rework)
const activeLots = computed(() => lots.value.filter((lot) => lot.status === 'active').length)
const completedLots = computed(() => lots.value.filter((lot) => lot.status === 'completed').length)
const bottleneck = computed(() => findBottleneckStage(stages.value))
const scheduleRisk = computed(() => order.value ? evaluateProductionScheduleRisk(order.value) : null)
const handoverSignals = computed(() => [
  ...(remaining.value > 0 ? [`${remaining.value} units remain against the order plan.`] : []),
  ...(bottleneck.value ? [`${bottleneck.value.stage_name} is the lowest-progress open stage at ${stagePercent(bottleneck.value)}%.`] : []),
  ...(rejectedTotal.value > 0 ? [`${rejectedTotal.value} rejected units require supervisor review.`] : []),
  ...(reworkTotal.value > 0 ? [`${reworkTotal.value} units remain in rework flow.`] : []),
  ...(activeLots.value > 0 ? [`${activeLots.value} production lots remain active.`] : []),
])

function stagePercent(stage: ProductionOrderStage) {
  return stageCompletionPercent(stage)
}
function printHandover() {
  handoverAt.value = new Date().toLocaleString()
  window.print()
}
function safeFileName(extension: string) {
  return `${order.value?.mo_code || `production-order-${id.value}`}-handover.${extension}`.replace(/[^a-z0-9._-]+/gi, '-')
}
function downloadFile(content: string, type: string, fileName: string) {
  const url = URL.createObjectURL(new Blob([content], { type }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  anchor.click()
  URL.revokeObjectURL(url)
}
function refreshSnapshot() {
  const snapshot = new Date().toISOString()
  handoverAt.value = new Date(snapshot).toLocaleString()
  return snapshot
}
function exportCsv() {
  if (!order.value) return
  downloadFile(
    buildProductionOrderCsv(order.value, stages.value, lots.value, refreshSnapshot()),
    'text/csv;charset=utf-8',
    safeFileName('csv'),
  )
  exportMessage.value = 'CSV handover exported.'
}
async function shareHandover() {
  if (!order.value) return
  const text = buildHandoverText(order.value, stages.value, lots.value, refreshSnapshot())
  const file = new File([text], safeFileName('txt'), { type: 'text/plain' })
  const shareData: ShareData = { title: `Production order ${order.value.mo_code}`, text, files: [file] }
  try {
    if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
      await navigator.share(shareData)
      exportMessage.value = 'Handover shared.'
      return
    }
  } catch (cause) {
    if ((cause as DOMException)?.name === 'AbortError') return
  }
  downloadFile(text, 'text/plain;charset=utf-8', file.name)
  exportMessage.value = 'Sharing is unavailable, so the handover text was downloaded.'
}
async function load() {
  loading.value = true
  error.value = ''
  try {
    [order.value, stages.value, lots.value] = await Promise.all([
      getProductionOrder(client, id.value),
      getProductionOrderStages(client, id.value),
      listProductionLots(client, id.value),
    ])
    handoverAt.value = new Date().toLocaleString()
  } catch (cause) { error.value = formatApiError(cause) }
  finally { loading.value = false }
}
onMounted(load)
</script>

<template>
  <div class="page-toolbar">
    <RouterLink class="back" data-tooltip="Back to production orders" to="/production-orders">Back to production orders</RouterLink>
    <div v-if="order" class="page-toolbar__actions actions">
      <button class="action secondary" type="button" @click="exportCsv">Export CSV</button>
      <button class="action secondary" type="button" @click="shareHandover">Share handover</button>
      <button class="print" type="button" @click="printHandover">Print handover</button>
      <RouterLink class="action" :to="`/production-orders/${order.id}/stages`">Open stage board</RouterLink>
      <RouterLink class="action primary" :to="`/production-orders/${order.id}/lots`">Open lot queue</RouterLink>
    </div>
  </div>
  <p v-if="exportMessage" class="export-message" role="status">{{ exportMessage }}</p>
  <div v-if="loading" class="state">Loading order...</div>
  <div v-else-if="error" class="state error">{{ error }}</div>
  <template v-else-if="order">
    <header>
      <div><p class="eyebrow">Production order</p><h2>{{ order.mo_code }}</h2><p>{{ order.product_name || `Product #${order.product_id}` }}</p></div>
    </header>
    <div v-if="scheduleRisk" class="schedule-alert" :class="scheduleRisk.level" role="alert"><strong>Schedule attention:</strong> {{ scheduleRisk.label }}. {{ remaining }} units remain.</div>
    <section class="summary">
      <div><span>Status</span><strong>{{ order.status.replace('_', ' ') }}</strong></div>
      <div><span>Plan</span><strong>{{ planned }}</strong></div>
      <div><span>Actual</span><strong>{{ produced }}</strong></div>
      <div :class="{ warning: remaining > 0 }"><span>Remaining</span><strong>{{ remaining }}</strong></div>
      <div><span>Completion</span><strong>{{ completionPercent }}%</strong></div>
      <div><span>Active lots</span><strong>{{ activeLots }}</strong></div>
      <div :class="{ exception: rejectedTotal > 0 }"><span>Rejected</span><strong>{{ rejectedTotal }}</strong></div>
      <div :class="{ warning: reworkTotal > 0 }"><span>Rework</span><strong>{{ reworkTotal }}</strong></div>
    </section>
    <section class="handover">
      <div class="section-heading">
        <div><p class="eyebrow">Shift handover</p><h3>Supervisor attention</h3></div>
        <small>Snapshot: {{ handoverAt }}</small>
      </div>
      <p v-if="!handoverSignals.length" class="clear">No open production exceptions. Order plan is complete.</p>
      <ul v-else><li v-for="signal in handoverSignals" :key="signal">{{ signal }}</li></ul>
      <div class="lot-stats"><span>{{ activeLots }} active lots</span><span>{{ completedLots }} completed lots</span><span>{{ lots.length }} total lots</span></div>
    </section>
    <section>
      <div class="section-heading"><h3>Plan versus actual by stage</h3><span v-if="bottleneck" class="bottleneck">Bottleneck: {{ bottleneck.stage_name }}</span></div>
      <div v-if="!stages.length" class="empty">No BOM operation stages are available.</div>
      <div v-else class="stages">
        <article v-for="stage in stages" :key="stage.id" :class="{ bottleneck_stage: bottleneck?.id === stage.id }">
          <div class="stage-title"><span class="sequence">{{ stage.operation_sequence }}</span><div><strong>{{ stage.stage_name }}</strong><small>{{ stage.stage_code }}</small></div><span class="badge">{{ stage.status.replace('_', ' ') }}</span></div>
          <div class="stage-metrics"><span>Plan <strong>{{ stage.planned_quantity }}</strong></span><span>Actual <strong>{{ stage.completed_quantity }}</strong></span><span>Variance <strong>{{ Math.max(0, stage.planned_quantity - stage.completed_quantity) }}</strong></span><span>Yield <strong>{{ stagePercent(stage) }}%</strong></span></div>
          <progress :value="stage.completed_quantity" :max="stage.planned_quantity"></progress>
          <p>Rejected {{ stage.rejected_quantity }} · Rework {{ stage.rework_quantity }}<template v-if="stage.assigned_employee_name"> · {{ stage.assigned_employee_name }}</template></p>
        </article>
      </div>
    </section>
  </template>
</template>

<style scoped>
.back{display:inline-block;margin-bottom:20px;color:#285b9a;font-weight:700}header{display:flex;align-items:center;justify-content:space-between;gap:20px}.eyebrow{margin:0;color:#66726e;font-size:12px;font-weight:800;text-transform:uppercase}h2{margin:4px 0}.actions{display:flex;gap:8px}.action,.print{padding:10px 14px;border:0;color:#fff;background:#3568a8;font-weight:700;text-decoration:none;cursor:pointer}.action.secondary{color:#285b9a;background:#eaf1f8}.primary,.print{background:#07996f}.export-message{margin:0 0 12px;padding:9px 12px;color:#236451;background:#eaf7f2;font-weight:700}.schedule-alert{margin:14px 0 0;padding:12px 14px;border-left:4px solid}.schedule-alert.warning{border-color:#d49a22;color:#765213;background:#fff3c9}.schedule-alert.critical{border-color:#c64a39;color:#852d22;background:#ffe1dc}.summary{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:16px 0}.summary div,section{padding:18px;border:1px solid #d7dfdc;background:#fff}.summary div{display:grid;gap:5px}.summary span,small{color:#66726e}.summary .exception{border-color:#efb5ad;background:#fff3f1}.summary .warning{border-color:#ecd28a;background:#fffae9}.section-heading{display:flex;align-items:center;justify-content:space-between;gap:12px}.section-heading h3{margin:0}.handover{margin-bottom:16px}.handover ul{display:grid;gap:7px;margin:14px 0;padding-left:20px}.handover li::marker{color:#c16b1b}.clear{color:#236451;font-weight:700}.lot-stats{display:flex;gap:8px;flex-wrap:wrap}.lot-stats span,.bottleneck{padding:6px 9px;color:#6b4b12;background:#fffae9;font-size:12px;font-weight:800}.stages{display:grid;gap:9px;margin-top:14px}.stages article{padding:12px;border:1px solid #e1e7e5;background:#f8faf9}.stages article.bottleneck_stage{border-color:#e2b65d;background:#fffae9}.stage-title{display:flex;align-items:center;gap:9px}.stage-title>div{display:grid}.sequence{display:grid;width:28px;height:28px;color:#fff;background:#3568a8;place-items:center}.badge{margin-left:auto;padding:5px 8px;color:#236451;background:#eaf7f2;text-transform:capitalize}.stage-metrics{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-top:10px}.stage-metrics span{display:grid;color:#66726e;font-size:12px}.stage-metrics strong{color:#14243a;font-size:16px}.stages progress{width:100%;margin-top:10px}.stages p{margin:5px 0 0;color:#53625d;font-size:13px}.state{display:grid;min-height:220px;place-items:center}.error{color:#852d22}@media(max-width:800px){header,.section-heading{align-items:stretch;flex-direction:column}.actions{flex-wrap:wrap}.summary{grid-template-columns:repeat(2,1fr)}}@media(max-width:520px){.actions{display:grid}.stage-metrics{grid-template-columns:repeat(2,1fr)}}@media print{.back,.actions,.export-message{display:none!important}header{margin-bottom:14px}section{break-inside:avoid}.handover{border:2px solid #14243a}}
</style>
